import { useState } from "react";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import Chip from "@mui/material/Chip";
import { useTheme } from "@mui/material/styles";
import { useAtomValue } from "jotai";

import { StoryItem } from "./StoryItem";
import { DarkStoryPreview } from "./StoryDetails";
import { storyCaption } from "./storydat";
import { MentionResolvers } from "./ppToChipText";
import { peopleIndexState, activeObjectsState } from "./JotaiAtoms";

const LABEL_MAX = 40;
const MAX_VISIBLE_CHIPS = 5;

const chipLabel = (story: StoryItem, resolvers: MentionResolvers): string => {
  const text = storyCaption(story, resolvers);
  return text.length > LABEL_MAX ? text.slice(0, LABEL_MAX) + "…" : text;
};

/**
 * Sort priority (descending):
 * 1) in leader cache  2) has st_cap  3) st_pp length  4) st_files count
 * 5) st_len + st_thc
 */
function sortKey(story: StoryItem, cachedIds: string[]): number[] {
  return [
    cachedIds.includes(story.SK) ? 1 : 0,
    story.st_cap ? 1 : 0,
    Array.isArray(story.st_pp) ? story.st_pp.length : 0,
    (story as any).st_files?.length ?? 0,
    (story.st_len ?? 0) + (story.st_thc ?? 0),
  ];
}

function sortedOthers(stories: StoryItem[], cachedIds: string[]): StoryItem[] {
  return [...stories].sort((a, b) => {
    const ka = sortKey(a, cachedIds);
    const kb = sortKey(b, cachedIds);
    for (let i = 0; i < ka.length; i++) {
      if (kb[i] !== ka[i]) return kb[i] - ka[i];
    }
    return 0;
  });
}

type JoinLeaderPickerProps = {
  stories: StoryItem[];
  onLeaderChange: (id: string) => void;
  initialLeaderId?: string;
  cachedIds?: string[];
};

export function JoinLeaderPicker({
  stories,
  onLeaderChange,
  initialLeaderId,
  cachedIds = [],
}: JoinLeaderPickerProps) {
  const initialIdx = initialLeaderId
    ? Math.max(
        0,
        stories.findIndex((s) => s.SK === initialLeaderId)
      )
    : 0;
  const [leaderIdx, setLeaderIdx] = useState(initialIdx);
  const [previewIdx, setPreviewIdx] = useState(initialIdx);
  const theme = useTheme();
  const people = useAtomValue(peopleIndexState);
  const objects = useAtomValue(activeObjectsState);

  const buildResolvers = (story: StoryItem): MentionResolvers => ({
    resolveUser: (userId) => {
      const app = story.st_source ?? "slack";
      const accounts = people.get(`${app}:${userId}`);
      if (!accounts?.length) return undefined;
      let name = "";
      for (const acc of accounts) {
        if (acc.name && acc.name.length > name.length) name = acc.name;
      }
      return name || accounts[0].email || undefined;
    },
    resolveUsergroup: (groupId) => {
      const obj = objects[`slack:usergroup:${groupId}`];
      return obj ? obj.name || obj.full_name : undefined;
    },
  });

  const handleChipClick = (story: StoryItem) => {
    const idx = stories.indexOf(story);
    setLeaderIdx(idx);
    setPreviewIdx(idx);
    onLeaderChange(story.SK);
  };

  const others = sortedOthers(
    stories.filter((_, i) => i !== leaderIdx),
    cachedIds
  );
  const visible = others.slice(0, MAX_VISIBLE_CHIPS);
  const hiddenCount = others.length - visible.length;

  return (
    <Stack direction="column" gap={0.75}>
      {others.length > 0 && (
        <>
          <Typography sx={{ fontSize: "11px", color: "var(--text-secondary)" }}>
            Use as leader instead:
          </Typography>
          <Stack
            direction="row"
            sx={{ flexWrap: "wrap", gap: 0.5, alignItems: "center" }}
          >
            {visible.map((story) => (
              <Chip
                key={story.SK}
                label={chipLabel(story, buildResolvers(story))}
                size="small"
                variant="outlined"
                onClick={() => handleChipClick(story)}
                onMouseEnter={() => setPreviewIdx(stories.indexOf(story))}
                onMouseLeave={() => setPreviewIdx(leaderIdx)}
                sx={{
                  fontSize: "11px",
                  cursor: "pointer",
                  borderColor:
                    theme.palette.mode === "dark"
                      ? "rgba(138,180,248,0.4)"
                      : "rgba(25,103,210,0.3)",
                  color: theme.palette.mode === "dark" ? "#8ab4f8" : "#1967d2",
                  "&:hover": {
                    borderColor:
                      theme.palette.mode === "dark" ? "#8ab4f8" : "#1967d2",
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? "rgba(138,180,248,0.08)"
                        : "rgba(25,103,210,0.06)",
                  },
                }}
              />
            ))}
            {hiddenCount > 0 && (
              <Typography
                sx={{ fontSize: "11px", color: "var(--text-secondary)" }}
              >
                +{hiddenCount} more
              </Typography>
            )}
          </Stack>
        </>
      )}
      <Typography
        sx={{
          fontSize: "11px",
          color: "var(--text-secondary)",
          fontWeight: 500,
        }}
      >
        {previewIdx === leaderIdx ? "Leader" : "Preview"}
      </Typography>
      <DarkStoryPreview item={stories[previewIdx]} />
    </Stack>
  );
}
